import { Button } from '@/components/ui/button';
import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useAuth } from '@/hooks/useAuth';
import { 
  collapseDrawer, 
  selectCopilotState, 
  setActiveTab
} from '@/store/slices/copilot/copilotSlice';
import {
  selectCurrentConversationId,
  setCurrentConversation
} from '@/store/slices/copilot/copilotChatSlice';
import { selectSelectedProfile } from '@/store/slices/copilot-profiles/copilotProfilesSlice';
import type { CopilotTab } from '@/store/slices/copilot/copilotSlice';
import { CopilotCollapseTab } from './CopilotCollapseTab';
import { ChatContainer } from './chat/ChatContainer';
import { cn } from '@/lib/utils';
import { GlowIndicator } from './GlowIndicator';
import { ConversationHistoryList } from './chat/ConversationHistoryList';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { CompactNewChatButton } from './chat/CompactNewChatButton';
// OLD: import CopilotKitChat from './CopilotKitChat';
// OLD: import BrandedCopilotChat from './BrandedCopilotChat';
import MCPCopilot from './MCPCopilot';

export const CopilotDrawer = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isOpen, isCollapsed, activeTab } = useAppSelector(selectCopilotState);
  const conversationId = useAppSelector(selectCurrentConversationId);
  const activeProfile = useAppSelector(selectSelectedProfile);
  const { isAuthenticated, user } = useAuth();
  const [hasAnimatedIn, setHasAnimatedIn] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [ripples, setRipples] = useState<{ [key: string]: { x: number; y: number; id: number } }>({});
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  
  useEffect(() => {
    // dispatch(loadMockConversations());
  }, [dispatch]);
  

  useEffect(() => {
    if (isOpen && !isCollapsed) {
      setTimeout(() => setHasAnimatedIn(true), 300);
      // Trigger scroll to bottom when widget opens
      setTimeout(() => {
        if ((window as any).clearCopilotChat) {
          // Trigger a scroll by calling the global scroll function
          if ((window as any).scrollCopilotToBottom) {
            (window as any).scrollCopilotToBottom();
          }
        }
      }, 400);
    } else {
      setHasAnimatedIn(false);
    }
  }, [isOpen, isCollapsed]);

  if (!isAuthenticated || !isOpen) {
    return null;
  }
  
  if (isCollapsed) {
    return <CopilotCollapseTab />;
  }

  const handleTabChange = (value: CopilotTab) => {
    dispatch(setActiveTab(value));
    
    if (value === 'history') {
      setShowArchived(false);
      // dispatch(loadMockConversations());
    }
  };

  const handleTabClick = (value: CopilotTab, event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const id = Date.now();
    
    setRipples(prev => ({
      ...prev,
      [value]: { x, y, id }
    }));
    
    // Clean up ripple after animation
    setTimeout(() => {
      setRipples(prev => {
        const newRipples = { ...prev };
        delete newRipples[value];
        return newRipples;
      });
    }, 600);
    
    handleTabChange(value);
  };

  const handleNewConversation = async () => {
    if (isCreatingConversation) return; // Prevent double clicks

    setIsCreatingConversation(true);

    try {
      dispatch(setActiveTab('chat'));

      const startNewConversation =
        typeof (window as any).__startNewCopilotConversation === 'function'
          ? (window as any).__startNewCopilotConversation
          : null;

      if (!startNewConversation) {
        throw new Error('Copilot chat is not ready to start a new conversation.');
      }

      await startNewConversation();
    } catch (error: any) {
      console.error('Error starting new conversation:', error);
      dispatch(setActiveTab('chat'));
    } finally {
      setIsCreatingConversation(false);
    }
  };

  const handleConversationCreated = (newConversationId: string) => {
    dispatch(setCurrentConversation(newConversationId));
  };

  const handleCollapseDrawer = () => {
    // Remove ticketId from URL when collapsing
    const urlParams = new URLSearchParams(window.location.search);
    
    if (urlParams.has('ticketId')) {
      urlParams.delete('ticketId');
      const newSearch = urlParams.toString();
      const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '');
      
      // Use window.history.replaceState instead of navigate to avoid potential routing issues
      const finalUrl = newUrl || window.location.pathname;
      window.history.replaceState(null, '', finalUrl);
    }
    
    dispatch(collapseDrawer());
  };

  const toggleArchivedView = () => {
    setShowArchived(!showArchived);
  };

  const userId = user?.id || 'anonymous';
  const profileName = activeProfile?.name || 'Pullse Copilot';
  
  
  return (
    <motion.div
      initial={{ x: 400, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 400, opacity: 0 }}
      transition={{ 
        type: 'spring',
        stiffness: 300,
        damping: 30,
        mass: 1
      }}
      className={cn(
        "fixed inset-y-0 right-0 w-full sm:w-[35vw] sm:min-w-[360px] overflow-hidden",
        "bg-gradient-to-b from-background/95 via-background to-background/95 backdrop-blur-xl",
        "border-l rounded-l-[30px]",
        "shadow-[0_2px_8px_rgba(0,0,0,0.08)]",
        "flex flex-col z-40"
      )}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-primary/[0.005] via-primary/[0.01] to-primary/[0.005] opacity-50" />
      <GlowIndicator className="opacity-10" />
      
      {/* Enhanced Header Section */}
      <motion.div 
        className={cn(
          "relative px-8 py-6 border-b",
          // Enhanced glassmorphism background
          "bg-gradient-to-r from-background/80 via-background/90 to-background/80 backdrop-blur-xl",
          // Multiple layered borders for depth
          "border-b-2 border-gradient-to-r from-border/10 via-border/30 to-border/10",
          "before:absolute before:inset-0 before:border-b before:border-white/10",
          "after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-gradient-to-r after:from-transparent after:via-primary/20 after:to-transparent",
          // Enhanced shadows and effects
          "shadow-[0_1px_3px_rgba(0,0,0,0.05),0_4px_12px_rgba(0,0,0,0.03)]",
          "relative overflow-hidden"
        )}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.4 }}
      >
        {/* Subtle animated background pattern */}
        <div className="absolute inset-0 opacity-[0.02]">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-primary/20 via-transparent to-secondary/20"
            animate={{
              x: ['-100%', '100%'],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        </div>

        {/* Floating ambient particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(4)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-primary/20"
              style={{
                left: `${15 + i * 25}%`,
                top: `${30 + (i % 2) * 40}%`,
              }}
              animate={{
                y: [-2, -6, -2],
                opacity: [0.2, 0.6, 0.2],
                scale: [0.8, 1.2, 0.8],
              }}
              transition={{
                duration: 3 + i * 0.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.8,
              }}
            />
          ))}
        </div>

        <div className="relative flex items-center justify-between z-10">
          {/* Enhanced title section */}
          <motion.div
            className="flex items-center gap-3"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            {/* Profile indicator dot */}
            <motion.div
              className="w-3 h-3 rounded-full bg-gradient-to-br from-primary/60 to-secondary/60 shadow-sm"
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.7, 1, 0.7],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            
            <h2 className={cn(
              "text-base font-semibold tracking-wide",
              "bg-gradient-to-r from-foreground via-foreground/90 to-foreground/80 bg-clip-text text-transparent",
              "relative"
            )}>
              {profileName}
              
              {/* Subtle text glow effect */}
              <span className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-secondary/10 blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </h2>
          </motion.div>
          
          {/* Enhanced action buttons */}
          <motion.div 
            className="flex items-center gap-3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <CompactNewChatButton 
                onClick={handleNewConversation} 
                loading={isCreatingConversation}
              />
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "relative h-9 w-9 rounded-xl",
                  // Enhanced glassmorphism
                  "bg-white/5 hover:bg-white/10 backdrop-blur-sm",
                  "border border-white/10 hover:border-white/20",
                  "shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.1)]",
                  // Smooth transitions
                  "transition-all duration-300 ease-out",
                  "group overflow-hidden"
                )}
                onClick={handleCollapseDrawer}
                aria-label="Collapse Copilot"
              >
                {/* Button background effects */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                {/* Ripple effect on hover */}
                <motion.div
                  className="absolute inset-0 bg-primary/10 rounded-xl opacity-0 group-hover:opacity-100"
                  initial={{ scale: 0 }}
                  whileHover={{ scale: 1 }}
                  transition={{ duration: 0.3 }}
                />
                
                <ChevronRight className="relative h-4 w-4 text-foreground/70 group-hover:text-foreground transition-colors duration-200" />
                
                {/* Shine effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full opacity-0 group-hover:opacity-100"
                  animate={{
                    translateX: ['-100%', '100%'],
                  }}
                  transition={{
                    duration: 0.6,
                    ease: "easeOut",
                  }}
                />
              </Button>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom highlight line */}
        <motion.div
          className="absolute bottom-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        />
      </motion.div>

      <div className="relative flex-1 overflow-hidden">
        <Tabs 
          value={activeTab} 
          onValueChange={handleTabChange} 
          className="w-full h-full flex flex-col"
        >
          <div className="relative px-6 py-4 bg-background/50 backdrop-blur-sm border-b border-border/30">
            <TabsList className={cn(
              "relative w-full h-11 p-1 bg-gradient-to-r from-muted/40 via-muted/30 to-muted/40 rounded-2xl",
              "border border-border/20 shadow-lg backdrop-blur-md",
              "before:absolute before:inset-0 before:rounded-2xl before:bg-gradient-to-r before:from-primary/5 before:via-transparent before:to-primary/5 before:opacity-0 before:transition-opacity before:duration-500",
              "hover:before:opacity-100"
            )}>
              {/* Animated background slider */}
              <motion.div
                layoutId="activeTabBg"
                className="absolute inset-y-1 rounded-xl bg-gradient-to-r from-background via-background/95 to-background shadow-md border border-border/30"
                style={{
                  left: activeTab === 'chat' ? '4px' : 'calc(50% + 2px)',
                  width: 'calc(50% - 6px)',
                }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 30,
                  mass: 0.8
                }}
              />

              <TabsTrigger 
                value="chat"
                onClick={(e) => handleTabClick('chat', e)}
                className={cn(
                  "relative flex-1 h-9 text-sm font-medium rounded-xl z-10",
                  "overflow-hidden transition-all duration-300 ease-out",
                  "data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground/80",
                  "data-[state=active]:text-foreground data-[state=active]:font-semibold",
                  "hover:scale-[1.02] active:scale-[0.98]",
                  "focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2",
                  "group"
                )}
              >
                <motion.span
                  className="relative z-20 flex items-center gap-2"
                  animate={{
                    scale: activeTab === 'chat' ? 1.05 : 1,
                  }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div
                    className="w-2 h-2 rounded-full bg-current opacity-0"
                    animate={{
                      opacity: activeTab === 'chat' ? 1 : 0,
                      scale: activeTab === 'chat' ? 1 : 0.5,
                    }}
                    transition={{ duration: 0.3 }}
                  />
                  Chat
                </motion.span>
                
                {/* Ripple effect */}
                {ripples.chat && (
                  <motion.div
                    className="absolute inset-0 z-10"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <motion.div
                      className="absolute bg-primary/20 rounded-full"
                      style={{
                        left: ripples.chat.x - 20,
                        top: ripples.chat.y - 20,
                        width: 40,
                        height: 40,
                      }}
                      initial={{ scale: 0, opacity: 0.8 }}
                      animate={{ scale: 2, opacity: 0 }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                    />
                  </motion.div>
                )}

                {/* Hover gradient */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />
              </TabsTrigger>

              <TabsTrigger 
                value="history"
                onClick={(e) => handleTabClick('history', e)}
                className={cn(
                  "relative flex-1 h-9 text-sm font-medium rounded-xl z-10",
                  "overflow-hidden transition-all duration-300 ease-out",
                  "data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground/80",
                  "data-[state=active]:text-foreground data-[state=active]:font-semibold",
                  "hover:scale-[1.02] active:scale-[0.98]",
                  "focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2",
                  "group"
                )}
              >
                <motion.span
                  className="relative z-20 flex items-center gap-2"
                  animate={{
                    scale: activeTab === 'history' ? 1.05 : 1,
                  }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div
                    className="w-2 h-2 rounded-full bg-current opacity-0"
                    animate={{
                      opacity: activeTab === 'history' ? 1 : 0,
                      scale: activeTab === 'history' ? 1 : 0.5,
                    }}
                    transition={{ duration: 0.3 }}
                  />
                  History
                </motion.span>

                {/* Notification dot - removed for now */}
                
                {/* Ripple effect */}
                {ripples.history && (
                  <motion.div
                    className="absolute inset-0 z-10"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <motion.div
                      className="absolute bg-primary/20 rounded-full"
                      style={{
                        left: ripples.history.x - 20,
                        top: ripples.history.y - 20,
                        width: 40,
                        height: 40,
                      }}
                      initial={{ scale: 0, opacity: 0.8 }}
                      animate={{ scale: 2, opacity: 0 }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                    />
                  </motion.div>
                )}

                {/* Hover gradient */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />
              </TabsTrigger>

              {/* Floating particles effect */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-1 h-1 bg-primary/20 rounded-full"
                    style={{
                      left: `${20 + i * 30}%`,
                      top: '50%',
                    }}
                    animate={{
                      y: [-10, -20, -10],
                      opacity: [0.3, 0.8, 0.3],
                      scale: [0.8, 1.2, 0.8],
                    }}
                    transition={{
                      duration: 2 + i * 0.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                ))}
              </div>
            </TabsList>
          </div>
          
          <div className="flex-1 overflow-hidden">
            {activeTab === 'chat' && (
              <TabsContent value="chat" className="m-0 h-full flex flex-col overflow-hidden">
                <div className="flex-1 min-h-0 overflow-hidden">
                  {/* NEW: Using MCP-based copilot */}
                  <MCPCopilot
                    className="h-full"
                    conversationId={conversationId}
                    onConversationCreated={handleConversationCreated}
                  />
                  {/* OLD: BrandedCopilotChat - kept for reference
                  <BrandedCopilotChat
                    className="h-full"
                    onClearChat={() => {}}
                    conversationId={conversationId}
                    onConversationCreated={handleConversationCreated}
                  />
                  */}
                </div>
              </TabsContent>
            )}

            <TabsContent value="history" className="m-0 h-full flex flex-col">
              <ConversationHistoryList
                activeConversationId={conversationId || ''}
                showArchived={showArchived}
                onToggleArchived={toggleArchivedView}
              />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </motion.div>
  );
};
